import React, {useEffect, useMemo, useState} from 'react';
import {FilePond} from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import {FilePondFile} from "filepond";
import {BehaviorSubject} from "rxjs";
import {useObservable} from "../../rxsupport";
import {
    detectScannedFileType,
    PDF_OR_WORD_TYPE_REJECTED_HINT,
    PDF_OR_WORD_TYPE_REJECTED_LABEL,
    PDF_TYPE_REJECTED_HINT,
    PDF_TYPE_REJECTED_LABEL,
} from "./scannedFileType";

type ScannedFileInputProps = {
    uploadedFiles: FilePondFile[];
    isFileValid$: BehaviorSubject<boolean>;
    shouldValidate$: BehaviorSubject<boolean>;
    setUploadedFiles: React.Dispatch<React.SetStateAction<FilePondFile[]>>;
    mustProvideBothPdfAndDoc?: boolean;
    mustIncludeVineland?: boolean;
    onlySupportPdf?: boolean;
    accessToken: string | undefined;
};

const filePondServerConfig = (token: string | undefined) => {
    return {
        revert: null,
        process: {
            url: `${process.env.REACT_APP_EXTERNAL_SCAN_VIRUS_SERVICE}/scan`,
            headers: {
                Authorization: token ? `Bearer ${token}` : ""
            }
        },
    }
}

const filePondLabelFileProcessingHandler = (err: any) => {
    if (err?.code === 406) {
        return "File contains a virus";
    }
    if (err?.code === 401) {
        return "Your session could be timeout, please re-login. If issue still persists, your access is not authorized (code 401), please contact admin support";
    }

    if (err?.body !== "") {
        return err?.body;
    }

    return "Unable to upload at the moment, please refresh the screen and re-try. If issue still persists, please contact admin support with the error details";
}

export const ScannedFileInput: React.FC<ScannedFileInputProps> = ({
                                                                      uploadedFiles,
                                                                      isFileValid$,
                                                                      shouldValidate$,
                                                                      setUploadedFiles,
                                                                      accessToken,
                                                                      mustProvideBothPdfAndDoc = false,
                                                                      onlySupportPdf = false,
                                                                      mustIncludeVineland = false,
                                                                  }) => {
    const numberOfFiles = useMemo(() => mustIncludeVineland || mustProvideBothPdfAndDoc ? 2 : 1, [mustIncludeVineland, mustProvideBothPdfAndDoc]);
    const [fileHasErr, setFileHasErr] = useState<{ id: string } | null>(null);
    const [hasMaliciousFile, setHasMaliciousFile] = useState(false);
    const [hasUploadedBefore, setHasUploadedBefore] = useState(false);
    const [missingPdfFile, setMissingPdfFile] = useState(false);
    const [missingDocFile, setMissingDocFile] = useState(false);
    const [hasNonPdfFile, setHasNonPdfFile] = useState(false);
    const [fileNumNotMet, setFileNumNotMet] = useState(false);
    const shouldValidate = useObservable(shouldValidate$, false);
    const [passVinelandCheck, setPassVinelandCheck] = useState(!mustIncludeVineland);
    // FilePond ids of files the type check rejected, by name or by bytes. A renamed file's name
    // still looks valid, so the name-based checks below can't see it.
    const [typeRejectedIds, setTypeRejectedIds] = useState<string[]>([]);
    const hasTypeRejectedFile = uploadedFiles.some(file => typeRejectedIds.includes(file.id));
    const typeRejectedLabel = onlySupportPdf ? PDF_TYPE_REJECTED_LABEL : PDF_OR_WORD_TYPE_REJECTED_LABEL;
    const typeRejectedHint = onlySupportPdf ? PDF_TYPE_REJECTED_HINT : PDF_OR_WORD_TYPE_REJECTED_HINT;

    useEffect(() => {
        if (uploadedFiles.length > 0) {
            setHasUploadedBefore(true);
        }

        // check all uploaded files are pdf
        const allFileProvidedInPdf = uploadedFiles.every(file => file.file.type === 'application/pdf');
        const hasPdf = uploadedFiles.some(file => file.file.type === 'application/pdf');
        const hasDoc = uploadedFiles.some(file =>
            file.file.type === 'application/msword' ||
            file.file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        );

        setMissingPdfFile(!hasPdf);
        setMissingDocFile(!hasDoc);
        setHasNonPdfFile(!allFileProvidedInPdf)

        const allValid = !uploadedFiles.some(file => fileHasErr && file.id === fileHasErr.id);
        setHasMaliciousFile(!allValid);
        if (!(mustProvideBothPdfAndDoc || mustIncludeVineland) && (shouldValidate || hasUploadedBefore)) {
            setFileNumNotMet(uploadedFiles.length === 0);
        }

        const passVinelandCheck = mustIncludeVineland
            ? (
                allFileProvidedInPdf
                && uploadedFiles.length === 2
                && uploadedFiles.some(file => file.file.name.toLowerCase().includes('vineland'))
            )
            : true;

        const isFileValid = allValid && uploadedFiles.length > 0
            && !hasTypeRejectedFile
            && (mustProvideBothPdfAndDoc ? hasPdf && hasDoc : true)
            && (onlySupportPdf ? allFileProvidedInPdf : true)
            && passVinelandCheck;

        isFileValid$.next(isFileValid);
        shouldValidate$.next(false);
        setPassVinelandCheck(passVinelandCheck);
    }, [shouldValidate, uploadedFiles, fileHasErr, hasTypeRejectedFile, mustProvideBothPdfAndDoc, mustIncludeVineland, isFileValid$, shouldValidate$, hasUploadedBefore]);

    let supportFileTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ]
    let fileValidateTypeLabelExpectedTypes = 'Expects PDF, DOC or DOCX'
    if (onlySupportPdf) {
        supportFileTypes = ['application/pdf'];
        fileValidateTypeLabelExpectedTypes = 'Expects PDF';
    }

    return (
        <div className="flex flex-col">
            <div className="flex">
                <div className="w-2/6 text-right">Upload File</div>
                <div className="ml-4 w-3/6">
                    <FilePond
                        allowMultiple={numberOfFiles > 1}
                        files={uploadedFiles.map(file => file.file)}
                        maxFileSize="20MB"
                        acceptedFileTypes={supportFileTypes}
                        // The plugin's typings name FilePond's own File class here, but at
                        // runtime the hook receives the browser File.
                        fileValidateTypeDetectType={(file, type) => detectScannedFileType(file as unknown as File, type)}
                        labelFileTypeNotAllowed={typeRejectedLabel}
                        onupdatefiles={setUploadedFiles}
                        onaddfile={(err, file) => {
                            // FilePond types this error as {type, code, body}, but a type
                            // rejection passes its row status {main, sub} instead.
                            if ((err as { main?: string } | null)?.main === typeRejectedLabel) {
                                setTypeRejectedIds(ids => ids.includes(file.id) ? ids : [...ids, file.id]);
                            }
                        }}
                        onprocessfile={(err, file) => {
                            if (err) {
                                setFileHasErr({id: file.id});
                                return;
                            }
                            if (fileHasErr && file.id === fileHasErr.id) {
                                setFileHasErr(null);
                            }
                        }}
                        maxFiles={numberOfFiles}
                        server={filePondServerConfig(accessToken)}
                        labelFileProcessingError={filePondLabelFileProcessingHandler}
                        fileValidateTypeLabelExpectedTypes={fileValidateTypeLabelExpectedTypes}
                        allowFileNameMiddleEllipsis
                        labelIdle='Drag & Drop or <span class="filepond--label-action">Browse</span>'
                    />
                </div>
            </div>
            {hasMaliciousFile && (
                <div className="w-full">
                    <div className="float-right w-4/6 pl-4 text-red-700">
                        You need to remove invalid files before submitting
                    </div>
                </div>
            )}
            {fileNumNotMet && (
                <div className="w-full">
                    <div className="float-right w-4/6 pl-4 text-red-700">
                        Need at least one file for submission
                    </div>
                </div>
            )}
            {
                (hasTypeRejectedFile || (onlySupportPdf && hasNonPdfFile)) && (
                    <div className="w-full">
                        <div className="float-right w-4/6 pl-4 text-red-700">
                            * {typeRejectedHint}
                        </div>
                    </div>
                )
            }
            {mustProvideBothPdfAndDoc && (
                <>
                    {missingDocFile && missingPdfFile && (
                        <div className="w-full">
                            <div className="float-right w-4/6 pl-4 text-red-700 text-xs">
                                * Need to provide both a DOC and a PDF document
                            </div>
                        </div>
                    )}
                    {missingDocFile && !missingPdfFile && (
                        <div className="w-full">
                            <div className="float-right w-4/6 pl-4 text-red-700">
                                Need at least one DOC or DOCX file for submission
                            </div>
                        </div>
                    )}
                    {missingPdfFile && !missingDocFile && (
                        <div className="w-full">
                            <div className="float-right w-4/6 pl-4 text-red-700">
                                Need at least one PDF file for submission
                            </div>
                        </div>
                    )}
                </>
            )}
            {!passVinelandCheck && (
                <div className="w-full">
                    <div className="float-right w-4/6 pl-4 text-red-700 text-xs">
                        * Need to include both the Vineland and the Diagnostic Evaluation Report
                    </div>
                </div>
            )}
        </div>)
        ;
}
